/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FiSearch, FiTrash2, FiUpload } from 'react-icons/fi'

import { ConfirmModal, Modal } from '@/components/ui'
import { useApi } from '@/hooks'

import { deleteImage, linkFloorPlan, renameImage, unlinkFloorPlan, uploadImage } from './api'
import { useColdStorage } from './ColdStorageContext'
import { useImageSrc } from './hooks'
import { FLOOR_PLAN_USAGE, type ImageMeta, type InStoreAsset } from './types'

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif'
const MAX_BYTES = 10 * 1024 * 1024

export const linkedAssetIds = (image: ImageMeta): number[] =>
  image.links.filter((l) => l.entityType === 'asset' && l.usage === FLOOR_PLAN_USAGE).map((l) => l.entityId)

/** The file's name without its extension: the default name for an uploaded image. */
export const defaultImageName = (fileName: string): string => fileName.replace(/\.[^.]+$/, '') || fileName

/** Assignment draft: location id → image id, or null for "no floor plan". */
export type Assignments = Record<number, number | null>

/** What every location shows today, the starting point of the draft. */
export const currentAssignments = (assets: InStoreAsset[], images: ImageMeta[]): Assignments =>
  Object.fromEntries(
    assets.map((asset) => [asset.id, images.find((image) => linkedAssetIds(image).includes(asset.id))?.id ?? null])
  )

const Thumb = ({ image, size = 'w-24 h-16', onClick }: { image: ImageMeta; size?: string; onClick?: () => void }) => {
  const src = useImageSrc(image.id)
  if (!src.data) return <div className={`skeleton ${size}`} />
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${size} shrink-0`}
      title="Click to enlarge"
      aria-label={`Preview ${image.name}`}
    >
      <img src={src.data} alt={image.name} className="w-full h-full object-contain bg-base-200 rounded" />
    </button>
  )
}

const Lightbox = ({ image, onClose }: { image: ImageMeta; onClose: () => void }) => {
  const src = useImageSrc(image.id)
  return (
    <div
      role="dialog"
      aria-label={`${image.name} preview`}
      onClick={onClose}
      className="fixed inset-0 z-[1000] bg-black/70 flex flex-col items-center justify-center gap-2 cursor-zoom-out p-6"
    >
      {src.data ? (
        <img src={src.data} alt={image.name} className="max-w-full max-h-[85vh] rounded shadow-lg bg-base-100" />
      ) : (
        <span className="loading loading-spinner text-white" />
      )}
      <span className="text-white text-sm">{image.name} · click to close</span>
    </div>
  )
}

/**
 * The locations in the assign view's order: those picked in the page's Location filter
 * first, then the rest, each group keeping the list's order. A search keeps the names
 * containing it, ignoring case.
 */
export const assignGroups = (
  assets: InStoreAsset[],
  selectedIds: number[],
  search: string
): { selected: InStoreAsset[]; others: InStoreAsset[] } => {
  const term = search.trim().toLowerCase()
  const matches = term ? assets.filter((asset) => asset.name.toLowerCase().includes(term)) : assets
  return {
    selected: matches.filter((asset) => selectedIds.includes(asset.id)),
    others: matches.filter((asset) => !selectedIds.includes(asset.id)),
  }
}

/**
 * The assign view: each location with a pick of the library, and the picked image's
 * thumbnail. The list scrolls inside the dialog so the buttons stay in reach however
 * many locations the company has.
 */
const AssignView = ({
  assets,
  selectedIds,
  images,
  draft,
  onChange,
  onPreview,
}: {
  assets: InStoreAsset[]
  /** The locations picked on the page, listed first. */
  selectedIds: number[]
  images: ImageMeta[]
  draft: Assignments
  onChange: (assetId: number, imageId: number | null) => void
  onPreview: (image: ImageMeta) => void
}) => {
  const [search, setSearch] = useState('')
  const { selected, others } = assignGroups(assets, selectedIds, search)

  const row = (asset: InStoreAsset) => {
    const imageId = draft[asset.id] ?? null
    const image = images.find((i) => i.id === imageId)
    return (
      <tr key={asset.id} data-testid={`assign-${asset.id}`}>
        <td className="font-medium">{asset.name}</td>
        <td>
          <select
            aria-label={`Floor plan for ${asset.name}`}
            className="select select-sm select-bordered w-64 max-w-full"
            value={imageId ?? ''}
            onChange={(e) => onChange(asset.id, e.target.value === '' ? null : Number(e.target.value))}
          >
            <option value="">No floor plan</option>
            {images.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </td>
        <td className="w-28">{image && <Thumb image={image} onClick={() => onPreview(image)} />}</td>
      </tr>
    )
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-base-content/70">
        Pick the floor plan each location shows. Locations in the same building can share one.
      </p>
      {assets.length === 0 ? (
        <p className="text-sm text-base-content/60">No locations for this company.</p>
      ) : (
        <>
          <label className="input input-sm input-bordered flex items-center gap-2 w-72 max-w-full">
            <FiSearch className="w-4 h-4 opacity-60" />
            <input
              type="search"
              aria-label="Search locations"
              placeholder="Search locations"
              className="grow"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <div className="max-h-[50vh] overflow-y-auto" data-testid="assign-list">
            {selected.length + others.length === 0 ? (
              <p className="text-sm text-base-content/60 py-2">No locations match.</p>
            ) : (
              <table className="table table-sm w-full">
                <thead className="sticky top-0 z-10 bg-base-100">
                  <tr>
                    <th>Location</th>
                    <th>Floor plan</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {selected.map(row)}
                  {selected.length > 0 && others.length > 0 && (
                    <tr aria-hidden data-testid="assign-divider">
                      <td colSpan={3} className="p-0">
                        <div className="border-t-2 border-base-content/30" />
                      </td>
                    </tr>
                  )}
                  {others.map(row)}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/** A library row: thumbnail, editable name, delete. */
const ImageRow = ({
  image,
  locationNames,
  onRename,
  onDelete,
  onPreview,
}: {
  image: ImageMeta
  locationNames: string[]
  onRename: (name: string) => Promise<void>
  onDelete: () => void
  onPreview: () => void
}) => {
  const [name, setName] = useState(image.name)
  useEffect(() => setName(image.name), [image.name])
  const commit = () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setName(image.name)
      return
    }
    if (trimmed !== image.name) void onRename(trimmed)
  }
  return (
    <div
      className="flex items-center gap-4 py-3 border-b border-base-300 last:border-b-0"
      data-testid={`image-${image.id}`}
    >
      <Thumb image={image} onClick={onPreview} />
      <div className="flex-1 min-w-0 space-y-1">
        <input
          aria-label={`Name of image ${image.id}`}
          className="input input-sm input-bordered w-full max-w-md"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
            if (e.key === 'Escape') setName(image.name)
          }}
        />
        <div className="text-xs text-base-content/60 truncate">
          {image.contentType} · {Math.round(image.byteSize / 1024)} KB ·{' '}
          {locationNames.length ? `shown by ${locationNames.join(', ')}` : 'not shown by any location'}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-ghost btn-sm btn-square text-error"
        aria-label={`Delete ${image.name}`}
        onClick={onDelete}
      >
        <FiTrash2 />
      </button>
    </div>
  )
}

/** The manage view: a drop zone, the name prompt for a new file, and the library. */
const ManageView = ({
  assets,
  images,
  onUpload,
  onRename,
  onDelete,
  onPreview,
  onReject,
}: {
  assets: InStoreAsset[]
  images: ImageMeta[]
  onUpload: (name: string, file: File) => Promise<void>
  onRename: (image: ImageMeta, name: string) => Promise<void>
  onDelete: (image: ImageMeta) => void
  onPreview: (image: ImageMeta) => void
  onReject: (reason: string) => void
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [pending, setPending] = useState<File | null>(null)
  const [pendingName, setPendingName] = useState('')
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState(false)

  const take = (file: File | undefined) => {
    if (!file) return
    if (!ACCEPT.split(',').includes(file.type)) {
      onReject('Floor plans must be PNG, JPEG, WebP or GIF.')
      return
    }
    if (file.size > MAX_BYTES) {
      onReject('Floor plans are limited to 10 MB.')
      return
    }
    setPending(file)
    setPendingName(defaultImageName(file.name))
  }

  const add = async () => {
    if (!pending || !pendingName.trim()) return
    setUploading(true)
    try {
      await onUpload(pendingName.trim(), pending)
      setPending(null)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-5">
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        data-testid="image-file"
        className="hidden"
        onChange={(e) => {
          take(e.target.files?.[0])
          e.target.value = ''
        }}
      />
      {pending ? (
        <div className="rounded-lg border border-base-300 p-4 space-y-3" data-testid="name-prompt">
          <div className="text-sm">
            Name for <span className="font-medium">{pending.name}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              aria-label="Image name"
              className="input input-sm input-bordered w-72"
              value={pendingName}
              autoFocus
              onChange={(e) => setPendingName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void add()
              }}
            />
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={!pendingName.trim() || uploading}
              onClick={() => void add()}
            >
              {uploading ? 'Adding…' : 'Add'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={uploading}
              onClick={() => setPending(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          data-testid="dropzone"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click()
          }}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault()
            setDragging(false)
            take(e.dataTransfer.files[0])
          }}
          className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 text-sm cursor-pointer transition-colors ${
            dragging ? 'border-primary bg-primary/10' : 'border-base-300 hover:border-primary/60'
          }`}
        >
          <FiUpload className="w-6 h-6" />
          <span>Drop an image here, or click to choose one</span>
          <span className="text-xs text-base-content/60">PNG, JPEG, WebP or GIF up to 10 MB</span>
        </div>
      )}
      <div>
        <h4 className="font-semibold text-sm mb-1">Images</h4>
        {images.length === 0 ? (
          <p className="text-sm text-base-content/60">No images uploaded for this company yet.</p>
        ) : (
          images.map((image) => (
            <ImageRow
              key={image.id}
              image={image}
              locationNames={assets.filter((a) => linkedAssetIds(image).includes(a.id)).map((a) => a.name)}
              onRename={(name) => onRename(image, name)}
              onDelete={() => onDelete(image)}
              onPreview={() => onPreview(image)}
            />
          ))
        )}
      </div>
    </div>
  )
}

/**
 * Floor plans, two views. **Assign**: every location with a pick of the company's images by
 * name; Save writes the links that changed (an image for the location, or none), Cancel
 * closes untouched. **Manage** (from the Manage images button) replaces it with the
 * library: a drop zone that asks for a name (the file name minus its extension) and adds
 * the image, then each image with an editable name and a delete. Deleting an image that
 * locations show asks first, since it takes their floor plan away. Close returns to
 * Assign, whose pick-lists follow the library.
 */
export const FloorPlansModal = () => {
  const { api } = useApi()
  const context = useColdStorage()
  const show = context.showFloorPlans

  const [view, setView] = useState<'assign' | 'manage'>('assign')
  const [draft, setDraft] = useState<Assignments>({})
  const [preview, setPreview] = useState<ImageMeta>()
  const [toDelete, setToDelete] = useState<ImageMeta>()
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const current = useMemo(() => currentAssignments(context.assets, context.images), [context.assets, context.images])

  useEffect(() => {
    if (show) {
      setView('assign')
      setDraft(currentAssignments(context.assets, context.images))
    }
    // Keyed on `show` alone: the draft must survive library changes while the dialog is open.
  }, [show])

  // A deleted image can no longer be picked: the locations that had it fall back to none.
  useEffect(() => {
    setDraft((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([assetId, imageId]) => [
          assetId,
          imageId != null && context.images.some((i) => i.id === imageId) ? imageId : null,
        ])
      )
    )
  }, [context.images])

  const close = useCallback(() => {
    context.setShowFloorPlans(false)
    setPreview(undefined)
    setToDelete(undefined)
  }, [context])

  const warn = (header: string, body: string) => context.notify({ header, body, position: 'end', warning: true })

  const save = async () => {
    setSaving(true)
    try {
      const changes = context.assets
        .map((asset) => ({ assetId: asset.id, from: current[asset.id] ?? null, to: draft[asset.id] ?? null }))
        .filter(({ from, to }) => from !== to)
      await Promise.all(
        changes.map(({ assetId, to }) => (to == null ? unlinkFloorPlan(api, assetId) : linkFloorPlan(api, assetId, to)))
      )
      await Promise.all([context.invalidateImages(), context.invalidateState()])
      context.notify({
        header: 'Floor plans saved',
        body: changes.length
          ? `${changes.length} location${changes.length === 1 ? '' : 's'} updated.`
          : 'Nothing changed.',
        position: 'end',
        success: true,
      })
      close()
    } catch {
      warn('Floor plans not saved', 'Something went wrong. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const upload = async (name: string, file: File) => {
    if (!context.companyId) return
    try {
      await uploadImage(api, context.companyId, name, file)
      await context.invalidateImages()
      context.notify({ header: 'Image added', body: `${name} is in the library.`, position: 'end', success: true })
    } catch {
      warn('Upload failed', 'Something went wrong. Please try again.')
    }
  }

  const rename = async (image: ImageMeta, name: string) => {
    try {
      await renameImage(api, image.id, name)
      await context.invalidateImages()
    } catch {
      warn('Rename failed', 'Something went wrong. Please try again.')
    }
  }

  const remove = (image: ImageMeta) => {
    if (linkedAssetIds(image).length > 0) {
      setToDelete(image)
    } else {
      void confirmDelete(image)
    }
  }

  const confirmDelete = async (image: ImageMeta) => {
    setDeleting(true)
    try {
      await deleteImage(api, image.id)
      await Promise.all([context.invalidateImages(), context.invalidateState()])
      context.notify({ header: 'Image deleted', body: `${image.name} was removed.`, position: 'end', success: true })
    } catch {
      warn('Delete failed', 'Something went wrong. Please try again.')
    } finally {
      setDeleting(false)
      setToDelete(undefined)
    }
  }

  const linkedCount = toDelete ? linkedAssetIds(toDelete).length : 0

  return (
    <>
      <Modal
        title={view === 'assign' ? 'Floor plans' : 'Manage images'}
        show={show}
        onHide={close}
        maxWidth="820px"
        footerButtons={
          view === 'assign'
            ? [
                { label: 'Manage images', onClick: () => setView('manage'), class: 'btn-ghost mr-auto' },
                { label: 'Cancel', onClick: close, class: 'btn-ghost' },
                {
                  label: 'Save',
                  onClick: () => void save(),
                  useModalVariant: true,
                  isLoading: saving,
                  disabled: saving,
                },
              ]
            : [{ label: 'Close', onClick: () => setView('assign'), useModalVariant: true }]
        }
        bodyContent={
          view === 'assign' ? (
            <AssignView
              assets={context.assets}
              selectedIds={context.locationIds}
              images={context.images}
              draft={draft}
              onChange={(assetId, imageId) => setDraft((prev) => ({ ...prev, [assetId]: imageId }))}
              onPreview={setPreview}
            />
          ) : (
            <ManageView
              assets={context.assets}
              images={context.images}
              onUpload={upload}
              onRename={rename}
              onDelete={remove}
              onPreview={setPreview}
              onReject={(reason) => warn('Image not accepted', reason)}
            />
          )
        }
      />
      {preview && <Lightbox image={preview} onClose={() => setPreview(undefined)} />}
      <ConfirmModal
        show={!!toDelete}
        title="Delete image"
        body={`This will remove the floor plan from ${linkedCount} location${linkedCount === 1 ? '' : 's'}.`}
        confirmLabel="Delete"
        variation="danger"
        isLoading={deleting}
        onConfirm={() => toDelete && void confirmDelete(toDelete)}
        onCancel={() => setToDelete(undefined)}
      />
    </>
  )
}
